// src/pages/Home/Home.tsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { carouselImageSet } from "../../assets/images/castsImages/carousel";
import { useFadeInScroll } from "../../components/Animation/fadeinScroll";

const Home = () => {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const [isDesktop, setIsDesktop] = useState(false);

  const title = useFadeInScroll<HTMLDivElement>();
  const body = useFadeInScroll<HTMLParagraphElement>();
  const actions = useFadeInScroll<HTMLDivElement>();

  /* Desktop detection */
  useEffect(() => {
    const check = () => setIsDesktop(window.innerWidth >= 1280);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  /* Carousel auto play */
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % carouselImageSet.length);
    }, 5200);
    return () => clearInterval(interval);
  }, []);

  /* Parallax only on desktop */
  useEffect(() => {
    if (!isDesktop) return;
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [isDesktop]);

  return (
    <section className="relative min-h-[80svh] flex items-center overflow-hidden bg-white">

      {/* Subtle grid texture */}
      <div
        className="
          absolute inset-0 pointer-events-none opacity-[0.035]
          bg-[linear-gradient(to_right,#000_1px,transparent_1px),linear-gradient(to_bottom,#000_1px,transparent_1px)]
          bg-[size:52px_52px]
        "
      />

      <div
        className="
          relative w-full max-w-8xl mx-auto
          px-6 sm:px-8 lg:px-10
          grid grid-cols-1 lg:grid-cols-2
          gap-15 xl:gap-14
          items-center
          py-20 lg:py-0 
        "
      >

        {/* LEFT CONTENT */}
        <div
          className="
            space-y-6
            max-w-2xl
            mx-auto lg:mx-0
            xl:-translate-y-4
             px-7 sm:px-7 lg:px-10
          "
        >
          <div
            ref={title.ref}
            className={`
              transition-all duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)]
              ${title.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}
            `}
          >
            <h2 className="text-hero text-primary font-primary">
              YESOAIR
            </h2>

          </div>

          <p
            ref={body.ref}
            className={`
              text-gray-600 leading-relaxed
              text-body text-text-secondary
              ${body.isVisible ? "opacity-100" : "opacity-0 translate-y-4"}
            `}
          >
            Replacing outdated casts with advanced, patient-friendly
            immobilizations that are breathable, waterproof, and built
            for modern life.
          </p>

          <div
            ref={actions.ref}
            className={`
              pt-5 flex flex-col sm:flex-row gap-5
              transition-all duration-[900ms] delay-300
              ${actions.isVisible ? "opacity-100" : "opacity-0 translate-y-4"}
            `}
          >
            <button
              onClick={() => navigate("/products", { state: { learnMode: true } })}
              className="
                px-9 py-4 rounded-2xl
                bg-primary text-white font-medium
                hover:scale-[1.03] active:scale-[0.98]
                transition-transform
              "
            >
              Get Started
            </button>

            <button
              onClick={() => navigate("/products", { state: { learnMode: true } })}
              className="
                px-9 py-4 rounded-2xl
                border border-gray-300
                text-gray-800 font-medium
                hover:bg-gray-50
                transition
              "
            >
              View Product
            </button>
          </div>

          <p className="pt-4 text-sm text-gray-500">
            CDSCO CERTIFIED – Sacrum-Pune-MH/M/MD/022990
          </p>
        </div>

        {/* RIGHT CAROUSEL */}
        <div
          className="
            relative flex items-center justify-center
            min-h-[360px]
            sm:min-h-[440px]
            lg:min-h-[520px]
            xl:min-h-[680px]
            xl:-translate-y-6
          "
        >
          {/* Glow */}
          <div
            className="
              absolute
              w-[300px] h-[300px]
              sm:w-[380px] sm:h-[380px]
              xl:w-[520px] xl:h-[520px]
              bg-primary/10 blur-[140px]
              rounded-full -z-10
            "
          />

          {carouselImageSet.map((img, index) => {
            const offset = index - activeIndex;

            if (!isDesktop && Math.abs(offset) > 1) return null;
            if (isDesktop && Math.abs(offset) > 2) return null;

            return (
              <img
                key={index}
                src={img}
                alt="3D printed orthopedic cast"
                className="
                  absolute w-full
                  max-w-[300px]
                  sm:max-w-[420px]
                  lg:max-w-[520px]
                  xl:max-w-[720px]
                  transition-all duration-[1200ms]
                  ease-[cubic-bezier(0.22,1,0.36,1)]
                "
                style={{
                  transform: `
                    translateX(${offset * (isDesktop ? 110 : 50)}px)
                    translateY(${isDesktop ? scrollY * 0.05 : 0}px)
                    scale(${1 - Math.abs(offset) * 0.1})
                  `,
                  opacity: offset === 0 ? 1 : 0,
                  zIndex: 10 - Math.abs(offset),
                }}
              />
            );
          })}

          {/* Dots */}
          <div className="absolute -bottom-10 flex gap-2">
            {carouselImageSet.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveIndex(i)}
                className={`h-2.5 w-2.5 rounded-full transition ${i === activeIndex ? "bg-gray-900" : "bg-gray-300"
                  }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Home;
